/* eslint-env browser */
// Add / edit a promo (wave 3, W2_Portal_Settings look): two cards (the promo,
// its discount and end date), one label above each field, what is wrong
// under the field, and a sticky save bar.
//
// Data: fetchPromos() once when the page opens, as before. The save is the
// old page's: the same checks in the same order, then the record with
// promo_code in capitals, promo_show true/false, createdAt = now,
// promo_validity as epoch ms (or null), without material-table's tableData,
// written with editPromo(record, 'Update' | 'Add'), then convertLanguage for
// the name and the description. Amounts and limits are numbers (an edited
// field is Number(text), an emptied one 0; untouched fields keep their
// stored value). The end date is entered in Addis Ababa time on a 12-hour
// clock and stored as before (ms). Saving needs critical edits on.
import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { api } from 'common';
import CircularLoading from '../components/CircularLoading';
import { showToast, confirmDialog, InlineMessage } from '../components/feedback';
import { BackButton, PageHeader, SectionCard, SaveBar, FieldLabel, FieldError, AddisDateTimeField, calmCaps, toMs } from '../components/modern';
import { tokens as c } from '../theme';

const TEXT_FIELDS = ['promo_name', 'promo_description', 'promo_code', 'promo_discount_type'];
const NUMBER_FIELDS = ['promo_discount_value', 'max_promo_discount_value', 'min_order', 'promo_usage_limit'];
const FIELD_ORDER = [
  'promo_name',
  'promo_code',
  'promo_description',
  'promo_usage_limit',
  'promo_discount_type',
  'promo_discount_value',
  'max_promo_discount_value',
  'min_order',
  'promo_validity',
];

const asText = (v) => (v === undefined || v === null ? '' : String(v));

export default function EditPromo() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { editPromo, convertLanguage, fetchPromos } = api;
  const settings = useSelector((s) => s.settingsdata.settings);
  const promos = useSelector((s) => s.promodata.promos);
  const auth = useSelector((s) => s.auth);
  const langLocale = auth?.profile?.lang?.langLocale ? auth.profile.lang.langLocale : null;

  const [original, setOriginal] = useState(null);
  const [draft, setDraft] = useState(null);
  const [numText, setNumText] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  // Promos are fetched on mount (no longer pre-fetched at login).
  useEffect(() => {
    dispatch(fetchPromos());
  }, [dispatch, fetchPromos]);

  // The list loads again when the page opens; what the person already typed
  // is kept when it arrives.
  const dirtyRef = useRef(false);
  useEffect(() => {
    if (dirtyRef.current) return;
    if (id) {
      if (promos) {
        const promoData = promos.filter((item) => item.id === id.toString())[0];
        if (!promoData) {
          navigate('/404');
          return;
        }
        setOriginal(promoData);
        setDraft({ ...promoData });
        setNumText({});
      }
    } else {
      const fresh = {
        createdAt: new Date().getTime(),
        promo_name: '',
        promo_description: '',
        promo_discount_type: '',
        promo_code: '',
        promo_discount_value: 0,
        max_promo_discount_value: 0,
        min_order: 0,
        promo_usage_limit: 0,
        user_avail: 0,
      };
      setOriginal(fresh);
      setDraft({ ...fresh });
      setNumText({});
    }
  }, [promos, id, navigate]);

  const backToList = () => navigate('/promos', { state: { pageNo: state?.pageNo } });

  // ── Values, changes and errors ────────────────────────────────────────
  const numberOf = (key) => {
    if (numText[key] !== undefined) return Number(String(numText[key]).replace(/,/g, '').trim());
    return draft ? draft[key] : undefined;
  };
  const shownNumber = (key) => (numText[key] !== undefined ? numText[key] : asText(draft ? draft[key] : ''));

  const changedKeys = (() => {
    if (!draft || !original) return [];
    const list = [];
    TEXT_FIELDS.forEach((key) => {
      if (asText(draft[key]) !== asText(original[key])) list.push(key);
    });
    Object.keys(numText).forEach((key) => {
      if (numberOf(key) !== original[key]) list.push(key);
    });
    if (toMs(draft.promo_validity) !== toMs(original.promo_validity)) list.push('promo_validity');
    return list;
  })();
  dirtyRef.current = changedKeys.length > 0;

  // The old checks, in the old order (0 or empty is not accepted).
  const errors = {};
  if (draft) {
    NUMBER_FIELDS.forEach((key) => {
      if (numText[key] === undefined) return;
      const n = numberOf(key);
      if (!Number.isFinite(n)) errors[key] = t('form_number_invalid', { defaultValue: 'Enter a number' });
      else if (n < 0) errors[key] = t('form_number_min', { min: 0, defaultValue: 'Enter {{min}} or more' });
    });
    const need = (key, msgKey, value) => {
      if (!errors[key] && !value) errors[key] = t(msgKey);
    };
    need('promo_name', 'promo_name_error', draft.promo_name);
    need('promo_code', 'promo_code_error', draft.promo_code);
    need('promo_description', 'promo_description_error', draft.promo_description);
    need('promo_discount_type', 'promo_discount_type_error', draft.promo_discount_type);
    need('promo_discount_value', 'promo_discount_value_error', numberOf('promo_discount_value'));
    need('max_promo_discount_value', 'max_promo_discount_value_error', numberOf('max_promo_discount_value'));
    need('min_order', 'min_order_error', numberOf('min_order'));
    if (
      !errors.max_promo_discount_value &&
      !errors.min_order &&
      draft.promo_discount_type === 'flat' &&
      numberOf('max_promo_discount_value') > numberOf('min_order')
    ) {
      errors.max_promo_discount_value = t('flat_minorder_maxorder');
    }
    // The old date picker did not accept a time in the past.
    if (changedKeys.includes('promo_validity') && toMs(draft.promo_validity) && toMs(draft.promo_validity) < Date.now()) {
      errors.promo_validity = t('promos_end_in_past', { defaultValue: 'Choose a time in the future' });
    }
    // A code another promo already uses. (The old check compared the code
    // with the list after removing that same code, so it never matched.)
    const code = String(draft.promo_code || '').trim().toUpperCase();
    if (
      !errors.promo_code &&
      code &&
      Array.isArray(promos) &&
      promos.some((p) => p && p.id !== id && String(p.promo_code || '').toUpperCase() === code)
    ) {
      errors.promo_code = t('code_already_avilable');
    }
  }
  const errorKeys = Object.keys(errors);
  const shown = (key) => {
    if (!errors[key]) return null;
    if (showErrors) return errors[key];
    return numText[key] !== undefined || changedKeys.includes(key) ? errors[key] : null;
  };

  const jumpToError = () => {
    setShowErrors(true);
    const first = FIELD_ORDER.find((key) => errors[key]);
    if (first) {
      const el = document.getElementById(`ep-${first}`);
      if (el && typeof el.focus === 'function') el.focus();
    }
  };

  const discard = async () => {
    const ok = await confirmDialog({
      title: t('form_discard_title', { defaultValue: 'Discard your changes?' }),
      body: t('form_discard_body', { defaultValue: 'What you typed here is not saved.' }),
      confirmLabel: t('settings_discard', { defaultValue: 'Discard' }),
      cancelLabel: t('confirm_cancel', { defaultValue: 'Cancel' }),
      destructive: true,
    });
    if (!ok) return;
    setDraft({ ...original });
    setNumText({});
    setShowErrors(false);
    setFormError(null);
  };

  // ── Save ──────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (saving || !draft) return;
    setFormError(null);
    if (!settings.AllowCriticalEditsAdmin) {
      showToast.warning(t('demo_mode'));
      return;
    }
    setShowErrors(true);
    if (errorKeys.length) {
      jumpToError();
      return;
    }
    if (!changedKeys.length) {
      setFormError(t('make_changes_to_update'));
      return;
    }
    const data = { ...draft };
    Object.keys(numText).forEach((key) => {
      data[key] = numberOf(key);
    });
    delete data.tableData;
    data.promo_code = data.promo_code.toUpperCase();
    data.promo_show = data.promo_show ? true : false;
    data.createdAt = new Date().getTime();
    data.promo_validity = data.promo_validity ? new Date(toMs(data.promo_validity) || data.promo_validity).getTime() : null;
    setSaving(true);
    if (id) {
      dispatch(editPromo(data, 'Update'));
    } else {
      dispatch(editPromo(data, 'Add'));
    }
    // Then the translations of the name and the description, as before.
    let translated = true;
    try {
      await convertLanguage(data.promo_name, langLocale);
      await convertLanguage(data.promo_description, langLocale);
    } catch (e) {
      translated = false;
    }
    if (translated) showToast.success(t('saved', { defaultValue: 'Saved' }));
    else showToast.warning(t('promos_saved_no_translation', { defaultValue: 'Saved. The other languages could not be updated.' }));
    if (id) backToList();
    else navigate('/promos');
  };

  if (!draft) return <CircularLoading />;

  // ── Fields ────────────────────────────────────────────────────────────
  const symbol = settings.symbol || '';
  const fieldBox = (key, label, control, span) => (
    <Box key={key} sx={{ minWidth: 0, ...(span ? { gridColumn: { md: 'span 2' } } : {}) }}>
      <FieldLabel htmlFor={`ep-${key}`} changed={changedKeys.includes(key)}>
        {label}
      </FieldLabel>
      {control}
      <FieldError id={`ep-${key}-error`}>{shown(key)}</FieldError>
    </Box>
  );
  const describedBy = (key) => (shown(key) ? `ep-${key}-error` : undefined);
  const textField = (key, label, opts = {}) =>
    fieldBox(
      key,
      label,
      <TextField
        id={`ep-${key}`}
        fullWidth
        value={asText(draft[key])}
        disabled={saving}
        error={!!shown(key)}
        multiline={!!opts.multiline}
        minRows={opts.multiline ? 2 : undefined}
        onChange={(e) => {
          const v = opts.upper ? e.target.value.toUpperCase() : e.target.value;
          setDraft((prev) => ({ ...prev, [key]: v }));
        }}
        inputProps={{ 'aria-describedby': describedBy(key), autoComplete: 'off', spellCheck: opts.upper ? false : undefined }}
      />,
      opts.span
    );
  const numberField = (key, label, adornment) =>
    fieldBox(
      key,
      label,
      <TextField
        id={`ep-${key}`}
        fullWidth
        value={shownNumber(key)}
        disabled={saving}
        error={!!shown(key)}
        onChange={(e) => {
          const v = e.target.value;
          setNumText((prev) => ({ ...prev, [key]: v }));
        }}
        inputProps={{ inputMode: 'decimal', autoComplete: 'off', 'aria-describedby': describedBy(key), 'aria-invalid': shown(key) ? 'true' : undefined }}
        InputProps={
          adornment
            ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <Box component="span" sx={{ color: c.INK_2, fontWeight: 600 }}>
                      {adornment}
                    </Box>
                  </InputAdornment>
                ),
              }
            : undefined
        }
        sx={{ '& input': { fontVariantNumeric: 'tabular-nums' } }}
      />
    );
  const grid = (children) => (
    <Box sx={{ display: 'grid', columnGap: 2.5, rowGap: 2.25, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, mt: 1.5 }}>
      {children}
    </Box>
  );
  const valueAdornment = draft.promo_discount_type === 'percentage' ? '%' : draft.promo_discount_type === 'flat' ? symbol : '';

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <BackButton onClick={backToList} />
      <PageHeader title={calmCaps(t(id ? 'update_promo_title' : 'add_promo_title'))} />
      {formError ? (
        <InlineMessage kind="error" onClose={() => setFormError(null)}>
          {formError}
        </InlineMessage>
      ) : null}

      {/* The two cards one above the other up to md (as before), side by side from lg. */}
      <Box sx={{ display: 'grid', gap: 2, alignItems: 'start', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' } }}>
        <SectionCard id="ep-promo" icon={<LocalOfferOutlinedIcon />} hue="accent" title={t('promos_card_promo', { defaultValue: 'Promo' })}>
          {grid([
            textField('promo_name', t('promo_name')),
            textField('promo_code', t('promo_code'), { upper: true }),
            textField('promo_description', t('description'), { multiline: true, span: true }),
            numberField('promo_usage_limit', t('promo_usage')),
          ])}
        </SectionCard>

        <SectionCard id="ep-discount" icon={<PaymentsOutlinedIcon />} hue="success" title={t('discount', { defaultValue: 'Discount' })}>
          {grid([
            fieldBox(
              'promo_discount_type',
              t('type'),
              <TextField
                id="ep-promo_discount_type"
                select
                fullWidth
                value={asText(draft.promo_discount_type)}
                disabled={saving}
                error={!!shown('promo_discount_type')}
                onChange={(e) => setDraft((prev) => ({ ...prev, promo_discount_type: e.target.value }))}
                SelectProps={{ SelectDisplayProps: { 'aria-describedby': describedBy('promo_discount_type') } }}
              >
                <MenuItem value="percentage">{t('percentage')}</MenuItem>
                <MenuItem value="flat">{t('flat')}</MenuItem>
              </TextField>
            ),
            numberField('promo_discount_value', t('promo_discount_value'), valueAdornment),
            numberField('max_promo_discount_value', t('max_limit'), symbol),
            numberField('min_order', t('min_limit'), symbol),
            fieldBox(
              'promo_validity',
              t('end_date'),
              <AddisDateTimeField
                id="ep-promo_validity"
                value={draft.promo_validity}
                onChange={(ms) => setDraft((prev) => ({ ...prev, promo_validity: ms }))}
                disabled={saving}
                error={!!shown('promo_validity')}
                describedBy={describedBy('promo_validity')}
              />,
              true
            ),
          ])}
        </SectionCard>
      </Box>

      <SaveBar
        changedCount={changedKeys.length}
        errorCount={showErrors ? errorKeys.length : 0}
        onJumpToError={jumpToError}
        onDiscard={discard}
        onSave={handleSave}
        saving={saving}
        saveLabel={id ? t('update', { defaultValue: 'Update' }) : calmCaps(t('add'))}
        sx={{ border: `1px solid ${c.OUTLINE}`, borderRadius: '12px' }}
      />
    </Box>
  );
}
